<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'invoice',
    path: '/invoices/:id',
    component: 'Invoice',
    params: [
      {
        name: 'id',
        prop: 'oid',
      },
    ],
  });
  addRoute({
    name: 'incomingInvoiceCreate',
    path: '/invoices-in/create',
    component: 'Invoice',
    componentProps: [
      {
        name: 'newInvoiceKind',
        value: 'incoming',
      },
    ],
  });
  addRoute({
    name: 'outgoingInvoiceCreate',
    path: '/invoices-out/create',
    component: 'Invoice',
    componentProps: [
      {
        name: 'newInvoiceKind',
        value: 'outgoing',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Invoice',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
    },
    registerEntry: {
      type: String,
      required: false,
    },
    formId: {
      type: String,
      required: false,
      default: function () {
        return uniqueID();
      },
    },
    newInvoiceKind: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const blankItem = ref(null);
  const item = ref(null);
  const currencies = ref(null);
  const formContext = ref({});
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'full-screen',
  }));
  const b_6466705138206980_class = computed(() => ({
    'bg-transparent': !props.modal,
    'shadow-sm': !props.modal,
  }));

  const $data = computed(() => ({
    blankItem: blankItem.value,
    item: item.value,
    currencies: currencies.value,
    formContext: formContext.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_6466705138206980_class: b_6466705138206980_class.value,
  }));

  const $$load_blankItem = async () => {
    return props.newInvoiceKind == null && props.registerEntry == null
      ? null
      : $fn.value.fetch('get_invoice_template', {
          parameters: {
            invoice_kind: props.newInvoiceKind,
            register_entry: props.registerEntry,
          },
          asResource: true,
        });
  };
  const $$load_item = async () => {
    return (
      blankItem.value ||
      $fn.value
        .fetch('read_invoice', { parameters: { id: props.oid } })
        .then((d) => {
          if (d.billing_period == null) {
            d.billing_period = {
              start: null,
              end: null,
            };
          }

          return new $fn.value.Resource(d);
        })
    );
  };
  const $$load_currencies = async () => {
    return $app.value.repo.get('list_currency', { map: true, key: 'code' });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'blankItem' },
      { name: 'item', dependsOn: ['blankItem'] },
      { name: 'currencies' },
      { name: 'formContext' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_6466705138206980_class', nodeId: '6466705138206980' },
    ],
    loaders: {
      blankItem: $$load_blankItem,
      item: $$load_item,
      currencies: $$load_currencies,
    },
    ux: {},
    dataRefs: {
      blankItem,
      item,
      currencies,
      formContext,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_6466705138206980_class,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isNew = computed(() => {
    if (loaded.value) {
      return !item.value || !item.value.id;
    }
    return null;
  });
  const invoiceTypeName = computed(() => {
    if (loaded.value) {
      return item.value.invoice_type === 'correction'
        ? $fn.value.pgettext('InvoiceType', 'Correction')
        : item.value.invoice_type === 'storno'
          ? $fn.value.pgettext('InvoiceType', 'Storno')
          : item.value.invoice_type === 'proforma'
            ? $fn.value.pgettext('InvoiceType', 'Pro Forma')
            : '?';
    }
    return null;
  });
  const currency = computed(() => {
    if (loaded.value) {
      return currencies.value[item.value.currency];
    }
    return null;
  });
  const exchangedCurrency = computed(() => {
    if (loaded.value) {
      return currencies.value[item.value.exchanged_currency];
    }
    return null;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: item.value.id
          ? item.value.invoice_number
          : $fn.value.gettext('Create Invoice'),
        icon: $config.value.invoice.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.registerEntry,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('registerEntry');
    }
  );
  watch(
    () => props.formId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formId');
    }
  );
  watch(
    () => props.newInvoiceKind,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('newInvoiceKind');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_1iggpp2o2_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6466705138206980_navigate_be2thqwbl_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.invoice',
        params: {
          id: $event.data.id,
        },
      },
    };
  };

  const act_7315092382398562_closeModal_1iggpp2o2 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_1iggpp2o2_getActionArgs,
    });
  const inst_evh_7315092382398562_close_jybkjjaxq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_1iggpp2o2,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_jybkjjaxq],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_cfk3v21hv =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_33v816vwx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_cfk3v21hv,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_33v816vwx],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_qlfs9a2a3 =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_bs6ogtllc =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_qlfs9a2a3,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_bs6ogtllc],
    event: 'close',
  });
  const act_6466705138206980_navigate_be2thqwbl =
    new actionTypes.NavigateAction({
      actionArgs: act_6466705138206980_navigate_be2thqwbl_getActionArgs,
    });
  const inst_evh_6466705138206980_created_pillm5gh5 =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_navigate_be2thqwbl,
      event: 'created',
      displayName: 'navigate',
    });
  const inst_evh_6466705138206980_created = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_created_pillm5gh5],
    event: 'created',
  });
  const act_6466705138206980_closeComponent_wzkgkt1t9 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_6466705138206980_deleted_uuf9sd68u =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeComponent_wzkgkt1t9,
      event: 'deleted',
      displayName: 'closeComponent',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_deleted_uuf9sd68u],
    event: 'deleted',
  });
  const act_6466705138206980_reloadComponentData_4g7o47k4y =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_b9rzt9v4i =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_4g7o47k4y,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_b9rzt9v4i],
    event: 'reload',
  });
  const act_5534025912102772_reloadComponentData_1j7yb49sq =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5534025912102772_reload_vn3v79rbn =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_reloadComponentData_1j7yb49sq,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5534025912102772_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_reload_vn3v79rbn],
    event: 'reload',
  });
  const act_1468474219929533_reloadComponentData_ykfkvosat =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_1468474219929533_reload_pomw8mhkm =
    new actionTypes.EventHandlerImpl({
      action: act_1468474219929533_reloadComponentData_ykfkvosat,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_1468474219929533_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1468474219929533_reload_pomw8mhkm],
    event: 'reload',
  });
  const act_2140198529698299_reloadComponentData_5ughnzzgx =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2140198529698299_reload_yv2wbsz3y =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_reloadComponentData_5ughnzzgx,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2140198529698299_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2140198529698299_reload_yv2wbsz3y],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_jybkjjaxq,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_33v816vwx,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_bs6ogtllc,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_created_pillm5gh5,
    inst_evh_6466705138206980_created,
    inst_evh_6466705138206980_deleted_uuf9sd68u,
    inst_evh_6466705138206980_deleted,
    inst_evh_6466705138206980_reload_b9rzt9v4i,
    inst_evh_6466705138206980_reload,
    inst_evh_5534025912102772_reload_vn3v79rbn,
    inst_evh_5534025912102772_reload,
    inst_evh_1468474219929533_reload_pomw8mhkm,
    inst_evh_1468474219929533_reload,
    inst_evh_2140198529698299_reload_yv2wbsz3y,
    inst_evh_2140198529698299_reload,
  };

  const evh_7315092382398562_close_jybkjjaxq = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_jybkjjaxq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_33v816vwx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_33v816vwx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_bs6ogtllc = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_bs6ogtllc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_created_pillm5gh5 = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_created_pillm5gh5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_created = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_created.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_uuf9sd68u = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_uuf9sd68u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_b9rzt9v4i = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_b9rzt9v4i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload_vn3v79rbn = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload_vn3v79rbn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_reload_pomw8mhkm = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_reload_pomw8mhkm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_reload = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_reload_yv2wbsz3y = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_reload_yv2wbsz3y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_reload = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    card
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Invoice"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.invoice.color"
        v-if="item"
        @close="evh_8564662037462133_close($event, {})"
      >
        <UcInvoiceHeader
          :invoice="item"
          :formContext="formContext"
          :class="b_6466705138206980_class"
          @created="evh_6466705138206980_created($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
          @reload="evh_6466705138206980_reload($event, {})"
        >
        </UcInvoiceHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpPanel
        collapsible
        :caption="pgettext('Invoice', 'Invoice data')"
        carousel
        class="mb-4"
        v-if="!isNew"
      >
        <UcInvoiceData
          :invoice="item"
          :reload="lastLoaded"
          @reload="evh_5534025912102772_reload($event, {})"
        >
        </UcInvoiceData>
      </ItpPanel>
      <div class="overflow-hidden w-100" v-if="currency && item">
        <UcInvoiceLines
          :invoice="item"
          :currency="currency"
          :defaultTaxCode="item.meta.default_tax_code"
          :defaultDiscount="item.meta.default_discount"
          :reload="lastLoaded"
          :formContext="formContext"
          class="h-100 w-100"
          @reload="evh_1468474219929533_reload($event, {})"
        >
        </UcInvoiceLines>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter
        :card="!modal"
        :modal="modal"
        :color="$config.invoice.color"
        v-if="item"
      >
        <UcInvoiceFooter
          :invoice="item"
          :currency="currency"
          :exchangedCurrency="exchangedCurrency"
          v-if="currency"
          @reload="evh_2140198529698299_reload($event, {})"
        >
        </UcInvoiceFooter>
      </UcFooter>
    </template>
  </ItpPage>
</template>
